import type { InboxRow } from '../api/tests';
import { ca } from '../i18n/ca';

/*
 * What a row of the tests page says about itself, in words: its name, where
 * it stands, and why it needs no review. Pure, so each sentence has a test.
 */

export const isCard = (row: InboxRow) =>
  row.type === 'play' || row.type === 'lend' || row.type === 'judge';

/** The number after a row's key: "campaign-18" is 18, "task-7" is 7. */
export const idOf = (row: InboxRow) => row.key.slice(row.key.indexOf('-') + 1);

/**
 * The row's name: a card's own title, and for everything else a sentence made
 * of what it is. The numbers and codes that tell one campaign or session from
 * another are the organiser's (`numbered`); a tester reads what it ran (the
 * owner, 2026-09-30).
 */
export function rowTitle(row: InboxRow, numbered = true): string {
  const t = ca.inbox;
  if (row.type === 'campaign') {
    return numbered
      ? t.campaignTitle(idOf(row), row.title, row.tours ?? 0)
      : t.campaignTitlePlain(row.title, row.tours ?? 0);
  }
  if (row.type === 'captures') return t.capturesTitle(numbered ? row.sessionCode : null);
  if (row.type === 'smoke') return t.smokeTitle(idOf(row), row.note);
  return row.title ?? '';
}

/** What is left to mark in a row that counts something; 0 when it counts nothing. */
export const left = (row: InboxRow) =>
  row.total != null && row.reviewed != null ? Math.max(0, row.total - row.reviewed) : 0;

/** The one word that says where the row stands, with the number when the number is the point. */
export function rowState(row: InboxRow): string {
  const t = ca.inbox;
  if (row.tray === 'review' && !isCard(row)) {
    return row.state === 'in_progress' ? t.inProgress(left(row)) : t.pending(left(row));
  }
  return t.states[row.state] ?? row.state;
}

/** Why a row needs no review, as a sentence; null for a row that says nothing. */
export function rowReason(row: InboxRow, numbered = true): string | null {
  const r = ca.inbox.reasons;
  switch (row.reason) {
    case 'superseded':
      return r.superseded(numbered ? row.supersededBy : null);
    case 'old':
      return r.old;
    case 'no_captures':
      return r.no_captures;
    case 'withheld':
      return r.withheld;
    case 'dismissed':
      return row.note ? `${r.dismissed}: ${row.note}` : r.dismissed;
    case 'cancelled':
      return r.cancelled;
    case 'session_ended':
      return r.session_ended;
    case 'automatic':
      return r.automatic;
    case 'reopened':
      return r.reopened;
    case 'older_version':
      return r.older_version;
    default:
      return null;
  }
}
